import { ComponentType } from "@angular/cdk/overlay";
import { inject, Injectable, TemplateRef } from "@angular/core";
import { MatDialog, MatDialogRef, MatDialogState } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { filter, firstValueFrom, takeUntil } from "rxjs";
import { ConfirmationDialogComponent } from "../../components/confirmation-dialog/confirmation-dialog.component";
import { GeneratePdfLoaderComponent } from "../../components/generate-pdf-loader/generate-pdf-loader.component";
import { SnackService } from "../snack-service.service";
import { TranslateUtils } from "../../utils/translate.utils";

/** Classe du panneau latéral d'exécution (géométrie et apparence : `app-global.scss`). */
export const SIDE_PANEL_CLASS = "justup-side-panel";
/** Sur `<body>` tant qu'un panneau latéral est ouvert : les cartes en direct (`vl-app-toasts`) se rangent à sa gauche. */
export const SIDE_PANEL_OPEN_CLASS = "justup-side-panel-open";
/** Largeur du panneau — la variable est définie une fois dans `app-global.scss` (les cartes en direct la lisent aussi). */
export const SIDE_PANEL_WIDTH = "var(--justup-side-panel-width, min(520px, 100vw))";
/** Brève surbrillance du panneau déjà ouvert quand on en redemande un. */
const SIDE_PANEL_NUDGE_CLASS = "justup-side-panel--nudge";
/** Pendant le déploiement depuis une carte (`origin`) : le panneau est caché sous sa « forme ». */
const SIDE_PANEL_MORPH_CLASS = "justup-side-panel--morph";
/** Déploiement carte → panneau (ms) : la forme grandit, puis le formulaire apparaît en fondu. */
export const SIDE_PANEL_MORPH_MS = 300;
export const SIDE_PANEL_REVEAL_MS = 160;
/** Rayon de la carte d'où part le déploiement (`vl-app-toasts`, `vl-task-item`). */
const MORPH_FROM_RADIUS_PX = 14;

/**
 * Contenu d'un panneau latéral qui sait dire s'il porte une saisie non enregistrée :
 * sans elle, il cède la place à la saisie demandée ensuite (règle « une saisie à la fois »).
 */
export interface SidePanelContent {
  hasPendingInput(): boolean;
}

export interface SidePanelOptions {
  /**
   * Ce qui est ouvert (ex. `ctl:<étape>:<contrôle>:<fin de fenêtre>`) : la même chose
   * redemandée (double tap, la carte puis la case de la frise) → le panneau ouvert reprend
   * le focus, rien de plus.
   */
  key?: string | null;
  /**
   * Ouverture AUTOMATIQUE (personne n'a rien demandé, ou l'enchaînement « Contrôle 2 sur 2 ») :
   * ne remplace jamais le panneau ouvert, aucun message. (Elle n'a jamais lieu pendant qu'on
   * tape dans un champ : `NotificationFacadeService.canAutoOpen`.)
   */
  quiet?: boolean;
  /**
   * D'où vient la demande — le rectangle de la carte « Réaliser » (`vl-app-toasts`) ou de la
   * ligne de « Mes tâches » : le panneau s'y DÉPLOIE (la forme grandit de la carte jusqu'à sa
   * place, puis le formulaire apparaît en fondu). Sans, ou `prefers-reduced-motion` : il
   * apparaît simplement.
   */
  origin?: DOMRect | null;
}

/**
 * Le panneau latéral ouvert. État de MODULE, pas du service : `DialogsService` est
 * ré-instancié par `SharedModule` (un par injecteur de composant autonome), la règle
 * doit valoir d'où que vienne l'ouverture.
 */
let sidePanel: { ref: MatDialogRef<unknown>; key: string | null } | null = null;

/** Mouvements réduits (réglage du système) : aucun déploiement animé. */
function reducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Ce panneau latéral céderait-il sa place à une autre saisie (ouvert, sans saisie en cours) ? */
export function sidePanelYields(ref: MatDialogRef<unknown> | null | undefined): boolean {
  if (!ref || sidePanel?.ref !== ref) return false;
  return !(ref.componentInstance as Partial<SidePanelContent> | null)?.hasPendingInput?.();
}

@Injectable({
  providedIn: "root",
})
export class DialogsService {
  private snack = inject(SnackService);
  private router = inject(Router, { optional: true });

  constructor(private dialog: MatDialog) {}

  open(
    c: ComponentType<any> | TemplateRef<any>,
    width: string = "fit-content",
    height: string = "fit-content",
    data?: any,
    closeOnNavigation = true,
    disableClose = false,
  ) {
    const dialogRef = this.dialog.open(c, {
      data,
      width,
      height,
      maxWidth: width,
      maxHeight: height,
      closeOnNavigation,
      autoFocus: false,
      disableClose,
    });
    if (disableClose) {
      dialogRef
        .backdropClick()
        .pipe(takeUntil(dialogRef.afterClosed()))
        .subscribe(async () => {
          const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
              title: "Attention",
              message: `Vous avez des modifications non sauvegardées, voulez-vous vraiment quitter la page ?`,
              confirmText: "Rester sur la page",
              cancelText: "Abandonner les modifications",
              values: {
                confirm: false,
                cancel: true,
                backdropClick: false,
              },
            },
          });
          const res = await firstValueFrom(confirmRef.afterClosed());
          if (res) {
            dialogRef.close();
          }
        });
    }
    return dialogRef;
  }

  openPanel(c: ComponentType<any> | TemplateRef<any>, data?: any) {
    return this.dialog.open(c, {
      data,
      position: {
        right: "0px",
      },
      panelClass: "justup-panel",
      width: "288px",
      height: "100%",
      closeOnNavigation: true,
      autoFocus: false,
    });
  }

  /**
   * PANNEAU D'EXÉCUTION à droite, SANS voile : la page dessous reste visible et utilisable.
   * Pleine hauteur sous la barre de navigation (la cloche et la messagerie restent accessibles) ;
   * pleine largeur sur un téléphone. Réservé à ce qui vient d'une NOTIFICATION (carte « Réaliser »,
   * « Mes tâches ») et à l'éditeur de liste ; une saisie ouverte depuis l'interface (bloc de frise,
   * case de contrôle, mode atelier) reste le popup centré (`open`) — retour du 28/09/2026.
   * `origin` : le panneau se DÉPLOIE depuis la carte cliquée (`morphFrom`).
   *
   * **Une saisie à la fois** — un panneau est déjà ouvert :
   * - la même chose redemandée (`key`) → il reprend le focus, rien ne s'ouvre ;
   * - autre chose, et il ne porte aucune saisie (`SidePanelContent.hasPendingInput`) → il est
   *   remplacé ;
   * - autre chose, et une saisie y est commencée → refusé : message, et il reprend le focus ;
   * - ouverture automatique (`quiet`) → refusée sans bruit.
   * Rend `null` quand rien ne s'ouvre.
   *
   * Sans voile, rien n'empêche plus de quitter la page : le panneau se ferme quand on change
   * de page (ou d'OF — l'URL change), pas seulement au retour arrière du navigateur. La croix
   * ferme comme avant ; Échap aussi, quand le focus est dans le panneau (ou nulle part) — Échap
   * tapé dans un champ de la page (recherche, messagerie) reste à ce champ : la couche CDK
   * l'écoute sur tout le document, il refermait le formulaire.
   */
  openSide<T, R = any>(c: ComponentType<T>, data?: any, opts: SidePanelOptions = {}): MatDialogRef<T, R> | null {
    const key = opts.key ?? null;
    const current = sidePanel;
    if (current && current.ref.getState() === MatDialogState.OPEN) {
      if (key && current.key === key) {
        if (!opts.quiet) this.nudge(current.ref);
        return null;
      }
      if (opts.quiet) return null;
      if (!sidePanelYields(current.ref)) {
        this.refuseSide();
        return null;
      }
      current.ref.close();
    }
    const top = this.navbarBottom();
    // Non modal : la page reste lisible par un lecteur d'écran. La couche CDK masque (`aria-hidden`)
    // tout le contenu hors dialogs à l'ouverture du PREMIER dialog, même non modal — on remet
    // les valeurs d'avant (elle les remettra elle-même à la fermeture du dernier).
    const pageAria = [...document.body.children]
      .filter((el) => !el.classList.contains("cdk-overlay-container"))
      .map((el) => [el, el.getAttribute("aria-hidden")] as const);
    const ref = this.dialog.open<T, any, R>(c, {
      data,
      position: { top: `${top}px`, right: "0" },
      width: SIDE_PANEL_WIDTH,
      maxWidth: "100vw",
      height: `calc(100dvh - ${top}px)`,
      maxHeight: `calc(100dvh - ${top}px)`,
      hasBackdrop: false,
      panelClass: SIDE_PANEL_CLASS,
      closeOnNavigation: true,
      // Échap géré ci-dessous (limité au panneau) ; sans voile, pas de clic extérieur à garder.
      disableClose: true,
      autoFocus: "dialog",
      restoreFocus: true,
      ariaModal: false,
    });
    for (const [el, value] of pageAria) {
      if (value === null) el.removeAttribute("aria-hidden");
      else el.setAttribute("aria-hidden", value);
    }
    sidePanel = { ref: ref as MatDialogRef<unknown>, key };
    document.body.classList.add(SIDE_PANEL_OPEN_CLASS);
    ref.afterClosed().subscribe(() => {
      // Remplacé entre-temps : le nouveau panneau garde sa place (et la classe du <body>).
      if (sidePanel?.ref !== ref) return;
      sidePanel = null;
      document.body.classList.remove(SIDE_PANEL_OPEN_CLASS);
    });
    ref
      .keydownEvents()
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((e) => {
        if (e.key !== "Escape" || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
        const target = e.target as Node | null;
        const container = document.getElementById(ref.id);
        if (target && target !== document.body && container && !container.contains(target)) return;
        e.preventDefault();
        ref.close();
      });
    if (this.router) {
      const openedAt = this.router.url;
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          takeUntil(ref.afterClosed()),
        )
        .subscribe((e) => {
          if (e.urlAfterRedirects !== openedAt) ref.close();
        });
    }
    if (opts.origin && !opts.quiet && !reducedMotion()) this.morphFrom(ref as MatDialogRef<unknown>, opts.origin);
    return ref;
  }

  /**
   * Ce contenu (`key`) est déjà ouvert dans le panneau latéral — le formulaire d'un créneau venu
   * d'une notification, puis le même bloc touché dans la frise : il reprend le focus (surbrillance)
   * et rien d'autre ne s'ouvre (pas deux formulaires pour la même échéance). `false` sinon.
   */
  focusSide(key: string | null | undefined): boolean {
    const current = sidePanel;
    if (!key || !current || current.key !== key || current.ref.getState() !== MatDialogState.OPEN) return false;
    this.nudge(current.ref);
    return true;
  }

  /**
   * DÉPLOIEMENT carte → panneau (FLIP) : une « forme » vide part du rectangle de la carte et
   * grandit jusqu'à celui du panneau (transform seul, composité ; le rayon est compensé de
   * l'échelle pour rester rond), le panneau caché dessous ; à l'arrivée elle s'efface et le
   * formulaire apparaît en fondu. La carte, elle, est retirée par son appelant dans la même image.
   */
  private morphFrom(ref: MatDialogRef<unknown>, from: DOMRect): void {
    // Par l'hôte du contenu : juste après `open`, l'`id` du conteneur n'est pas encore posé (liaison
    // d'hôte, au premier cycle de détection) — l'overlay, lui, est déjà attaché et dimensionné.
    const host = (ref.componentRef?.location?.nativeElement as HTMLElement | undefined) ?? document.getElementById(ref.id);
    const pane = host?.closest(".cdk-overlay-pane") as HTMLElement | null;
    if (!pane || typeof pane.animate !== "function") return;
    const to = pane.getBoundingClientRect();
    if (!to.width || !to.height || !from.width || !from.height) return;
    const sx = from.width / to.width;
    const sy = from.height / to.height;
    const ghost = document.createElement("div");
    ghost.className = "justup-side-panel-ghost";
    ghost.setAttribute("aria-hidden", "true");
    Object.assign(ghost.style, { top: `${to.top}px`, left: `${to.left}px`, width: `${to.width}px`, height: `${to.height}px` });
    document.body.appendChild(ghost);
    pane.classList.add(SIDE_PANEL_MORPH_CLASS);
    const morph = ghost.animate(
      [
        {
          transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${sx}, ${sy})`,
          borderRadius: `${MORPH_FROM_RADIUS_PX / sx}px / ${MORPH_FROM_RADIUS_PX / sy}px`,
        },
        { transform: "none", borderRadius: "0px" },
      ],
      { duration: SIDE_PANEL_MORPH_MS, easing: "cubic-bezier(0.2, 0, 0, 1)", fill: "forwards" },
    );
    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      ghost.remove();
      pane.classList.remove(SIDE_PANEL_MORPH_CLASS);
      pane.querySelector<HTMLElement>(".mat-mdc-dialog-surface > *")?.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: SIDE_PANEL_REVEAL_MS, easing: "ease-out" });
    };
    morph.onfinish = reveal;
    morph.oncancel = reveal;
    // Refermé pendant le déploiement (Échap, navigation) : la forme ne reste pas à l'écran.
    ref.afterClosed().subscribe(() => {
      morph.cancel();
      ghost.remove();
    });
  }

  /**
   * Une autre saisie va s'ouvrir (dialog de checklist) : le panneau latéral qui n'a pas de
   * saisie en cours lui cède la place. `false` : un panneau avec une saisie reste ouvert.
   */
  releaseSide(): boolean {
    const current = sidePanel;
    if (!current || current.ref.getState() !== MatDialogState.OPEN) return true;
    if (!sidePanelYields(current.ref)) return false;
    current.ref.close();
    return true;
  }

  /**
   * Un panneau latéral ouvert empêche une autre saisie : on le dit, et il reprend le focus
   * (brève surbrillance). `false` : aucun panneau latéral ouvert — rien n'a été dit.
   */
  refuseSide(): boolean {
    const current = sidePanel;
    if (!current || current.ref.getState() !== MatDialogState.OPEN) return false;
    this.snack.info(TranslateUtils.translate("system.side_panel_busy"));
    this.nudge(current.ref);
    return true;
  }

  private nudge(ref: MatDialogRef<unknown>): void {
    const container = document.getElementById(ref.id);
    container?.focus({ preventScroll: true });
    const pane = container?.closest(".cdk-overlay-pane");
    if (!pane) return;
    pane.classList.remove(SIDE_PANEL_NUDGE_CLASS);
    void (pane as HTMLElement).offsetWidth; // relance l'animation
    pane.classList.add(SIDE_PANEL_NUDGE_CLASS);
    setTimeout(() => pane.classList.remove(SIDE_PANEL_NUDGE_CLASS), 700);
  }

  /**
   * Bas de la barre de navigation du shell (0 sans barre : plein écran, écran de connexion).
   * Mesuré sur son conteneur : l'hôte `vl-navbar` est en ligne, sa boîte ne dit pas sa hauteur.
   */
  private navbarBottom(): number {
    const bottom = document.querySelector("vl-navbar")?.parentElement?.getBoundingClientRect().bottom ?? 0;
    return Number.isFinite(bottom) ? Math.max(0, Math.min(120, Math.round(bottom))) : 0;
  }

  async openPdfLoader(process: () => Promise<any>) {
    const dialogRef = this.dialog.open(GeneratePdfLoaderComponent);
    try {
      await process();
    } finally {
      dialogRef.close();
    }
  }
}
